<template>
  <div>
    <b-modal
      :id="MODAL_ID"
      :class="MODAL_ID"
      title="Extract Claims"
      size="lg"
      centered
      scrollable
      hide-footer
      @hide="close"
    >
      <!-- Tabs -->
      <b-tabs
        v-model="activeTab"
        content-class="mt-3"
      >
        <!-- Extract Tab -->
        <b-tab
          title="Extract"
          active
        >
          <div class="extract-tab">
            <!-- Configurable Prompt Section -->
            <div class="prompt-section mb-3">
              <div
                class="prompt-header d-flex justify-content-between align-items-center p-2 bg-light rounded cursor-pointer"
                @click="promptExpanded = !promptExpanded"
              >
                <span class="font-weight-bold">
                  <i :class="promptExpanded ? 'bi bi-chevron-down' : 'bi bi-chevron-right'" />
                  AI Prompt Configuration
                </span>
                <b-button
                  v-if="promptExpanded"
                  size="sm"
                  variant="outline-secondary"
                  @click.stop="resetPrompt"
                >
                  Reset to Default
                </b-button>
              </div>

              <b-collapse
                v-model="promptExpanded"
                class="mt-2"
              >
                <b-form-textarea
                  v-model="customPrompt"
                  rows="8"
                  max-rows="12"
                  placeholder="Enter custom prompt for AI extraction..."
                  class="mb-2"
                />
                <small class="text-muted">
                  Customize the prompt to change how the AI extracts and formats the text.
                </small>
              </b-collapse>
            </div>

            <!-- File Upload Area with Buttons -->
            <b-form-group
              label="Upload a PDF, JPG or PNG file to extract the claims text."
              class="flex-1"
            >
              <Vue2Dropzone
                id="excelDropzone"
                ref="fileInput"
                :options="dropzoneOptions"
                @vdropzone-file-added="setFile"
                @vdropzone-removed-file="removeFile"
              />
            </b-form-group>
            <button
              id="open_file_browser_btn"
              type="button"
              class="btn btn-outline-secondary"
            >
              <i class="bi bi-folder2-open" /> File Browser
            </button>
            <!-- Extract Button -->
            <div class="d-flex justify-content-end mb-3">
              <b-button
                variant="primary"
                :disabled="!selectedFile || isExtracting"
                @click="extractClaims"
              >
                <b-spinner
                  v-if="isExtracting"
                  small
                  class="mr-1"
                />
                Extract claims
              </b-button>
            </div>

            <!-- Loading Spinner -->
            <div
              v-if="isExtracting"
              class="text-center my-4"
            >
              <b-spinner
                variant="primary"
                style="width: 3rem; height: 3rem;"
              />
              <p class="mt-2 text-muted">
                Extracting text from file...
              </p>
            </div>

            <!-- Extracted Results -->
            <div
              v-if="extractedText"
              class="extracted-results mt-4"
            >
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6>Extracted Text:</h6>
                <div>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="mr-2"
                    @click="copyAllText"
                  >
                    <i class="bi bi-clipboard" /> Copy All
                  </b-button>
                  <b-dropdown
                    size="sm"
                    variant="outline-success"
                    text="Download"
                    right
                  >
                    <b-dropdown-item @click="downloadAsText">
                      <i class="bi bi-file-earmark-text" /> Download as TXT
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </div>

              <div class="extracted-text-container p-3 border rounded">
                <pre class="mb-0">{{ extractedText }}</pre>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- History Tab -->
        <b-tab title="History">
          <div class="history-tab">
            <!-- Loading state -->
            <div
              v-if="isLoadingHistory"
              class="text-center py-5"
            >
              <b-spinner variant="primary" />
              <p class="mt-2 text-muted">
                Loading history...
              </p>
            </div>

            <!-- No history -->
            <div
              v-else-if="history.length === 0"
              class="text-center text-muted py-5"
            >
              <p>No extraction history yet.</p>
            </div>

            <!-- History detail view -->
            <div v-else-if="selectedHistory">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <strong>{{ formatDate(selectedHistory.created_at) }}</strong>
                <div>
                  <b-dropdown
                    size="sm"
                    variant="outline-success"
                    text="Download"
                    class="mr-2"
                  >
                    <b-dropdown-item @click="downloadHistoryAsText(selectedHistory)">
                      <i class="bi bi-file-earmark-text" /> Download as TXT
                    </b-dropdown-item>
                  </b-dropdown>
                  <b-button
                    variant="link"
                    class="p-0 text-decoration-none"
                    @click="selectedHistory = null"
                  >
                    <i class="bi bi-arrow-left" /> Back
                  </b-button>
                </div>
              </div>

              <div
                class="history-detail p-3 border rounded"
                style="white-space: pre-wrap;"
              >
                <p class="mb-0">
                  {{ selectedHistory.result }}
                </p>
              </div>
            </div>

            <!-- History list view -->
            <div
              v-else
              class="history-list"
            >
              <div
                v-for="item in history"
                :key="item.id"
                class="history-item p-3 mb-3 border rounded"
                @click="openHistory(item)"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <strong class="d-block mb-1">{{ formatDate(item.created_at) }}</strong>
                    <p class="mb-0 text-muted history-text">
                      {{ truncateText(item.result, 50) }}
                    </p>
                  </div>
                  <b-button
                    size="sm"
                    variant="danger"
                    class="delete-btn ml-3"
                    @click.stop="confirmDeleteHistory(item.id)"
                  >
                    <i class="bi bi-trash" />
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>

      <!-- Footer -->
      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="close"
        >
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
import { EVENTS, extractClaimsEventBroker } from "@frontend/group/modules/ai-extract-claims/event-broker";
import Vue2Dropzone from "vue2-dropzone";

const MODAL_ID = 'ai_extract_claims_modal';

export default {
  name: "AiExtractClaimsModal",
  components: {
    Vue2Dropzone
  },
  data() {
    return {
      MODAL_ID,
      layoutId: null,
      activeTab: 0,
      selectedFile: null,
      isExtracting: false,
      isLoadingHistory: false,
      selectedHistory: null,
      extractedText: '',
      history: [],
      promptExpanded: false,
      customPrompt: '',
      defaultPrompt: '',
      dropzoneOptions: {
        url: "http",
        autoProcessQueue: false,
        addRemoveLinks: true,
        createImageThumbnails: true,
        acceptedFiles: ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png",
        maxFiles: 1,
        dictDefaultMessage:
          'Paste (CTRL +V), Drag & Drop your file here or <a href="#" onclick="event.preventDefault()">Browse</a>',
        init: function () {
          this.on("maxfilesexceeded", function(file) {
            this.removeAllFiles();
            this.addFile(file);
          });
        }
      },
    };
  },
  mounted() {
    extractClaimsEventBroker.on(EVENTS.INIT, this.init.bind(this));
    window.addEventListener('paste', this.handlePaste);

    $(document).on('click', '#submit', async () => {
      const $selectedItem = $('.grid-item.file-image-item.selected')
        .has('input.select-check:checked')
        .first();

      if (!$selectedItem.length) {
        console.warn('No image selected');
        return;
      }

      const imageUrl = $selectedItem.find('img').attr('src');

      console.log('Selected image:', imageUrl);
      
      await this.addImageUrlToDropzone(imageUrl);
    });
  },
  beforeDestroy() {
    if (this.$refs.fileInput && typeof $.fn.fileinput !== 'undefined') {
      $(this.$refs.fileInput).fileinput('destroy');
    }
    window.removeEventListener('paste', this.handlePaste);
  },
  methods: {
    init({ layoutId }) {
      this.layoutId = layoutId;
      this.$bvModal.show(MODAL_ID);
      this.loadHistory();
      this.loadCustomPrompt();
    },

    async loadCustomPrompt() {
      try {
        const response = await axios.get('/banner/groups/ai-extract-claims/prompt');
        if (response.data && response.data.prompt) {
          this.customPrompt = response.data.prompt;
          this.defaultPrompt = this.customPrompt;
        }
      } catch (error) {
        console.warn('Failed to load custom prompt, using default', error);
      }
    },

    resetPrompt() {
      this.customPrompt = this.defaultPrompt;
    },

    handlePaste(event) {
      if (!this.$bvModal || !document.getElementById(MODAL_ID)?.classList.contains('show')) {
        return;
      }

      const items = (event.clipboardData || event.originalEvent.clipboardData).items;

      for (let item of items) {
        if (item.type.indexOf('image') !== -1) {
          const blob = item.getAsFile();
          this.addFileFromBlob(blob, 'pasted-image.png');
          event.preventDefault();
          break;
        }
      }
    },

    addFileFromBlob(blob, filename) {
      const file = new File([blob], filename, { type: blob.type });

      if (this.$refs.fileInput && this.$refs.fileInput.dropzone) {
        const dropzone = this.$refs.fileInput.dropzone;
        dropzone.removeAllFiles();

        // Create resized thumbnail
        this.createThumbnail(blob, 200, 200).then(thumbnailUrl => {
          // Manually add file and emit event with resized thumbnail
          dropzone.emit("addedfile", file);
          dropzone.emit("thumbnail", file, thumbnailUrl);
          dropzone.emit("complete", file);
          dropzone.files.push(file);
        });
      }

      this.selectedFile = file;

      this.$bvToast.toast('Image pasted successfully', {
        title: 'Success',
        variant: 'success',
        autoHideDelay: 2000,
      });
    },

    createThumbnail(blob, maxWidth, maxHeight) {
      return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(blob);

        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const thumbnailUrl = canvas.toDataURL('image/png', 0.9);
          URL.revokeObjectURL(url);
          resolve(thumbnailUrl);
        };

        img.src = url;
      });
    },

    formatDate(date) {
      return new Date(date).toLocaleString();
    },

    truncateText(text, len) {
      if (!text) return "";
      return text.length > len ? text.slice(0, len) + "..." : text;
    },

    async loadHistory() {
      if (!this.layoutId) return;

      this.isLoadingHistory = true;
      try {
        const response = await axios.get(`/banner/groups/ai-extract-claims/history/${this.layoutId}`);
        this.history = response.data;
      } catch (error) {
        console.error("Failed to load history", error);
        this.$bvToast.toast('Failed to load extraction history', {
          title: 'Error',
          variant: 'danger',
        });
      } finally {
        this.isLoadingHistory = false;
      }
    },

    openHistory(item) {
      this.selectedHistory = item;
    },

    confirmDeleteHistory(id) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this history item?', {
        title: 'Confirm Delete',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel',
        centered: true
      })
        .then(value => {
          if (value) {
            this.deleteHistory(id);
          }
        });
    },

    async deleteHistory(id) {
      try {
        await axios.delete(`/banner/groups/ai-extract-claims/history/${id}`);
        this.history = this.history.filter(h => h.id !== id);
        this.$bvToast.toast('History item deleted successfully', {
          title: 'Success',
          variant: 'success',
          autoHideDelay: 2000,
        });
      } catch (error) {
        console.error("Failed to delete history", error);
        this.$bvToast.toast('Failed to delete history item', {
          title: 'Error',
          variant: 'danger',
        });
      }
    },

    setFile(file) {
      if (!file) return;
      this.selectedFile = file;
    },

    removeFile() {
      this.selectedFile = null;
      this.extractedText = '';
    },

    async extractClaims() {
      if (!this.selectedFile) return;

      this.isExtracting = true;
      this.selectedHistory = null;

      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        formData.append('prompt', this.customPrompt);

        const response = await axios.post(`/banner/groups/ai-extract-claims/${this.layoutId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const responseData = response.data;
        if (responseData.success) {
          this.extractedText = responseData.data.text;
          await this.loadHistory();
        } else {
          this.$bvToast.toast(responseData.message, {
            title: 'Error',
            variant: 'danger',
          });
        }
      } catch (error) {
        console.error('Extract claims error:', error);
        this.$bvToast.toast('An error occurred while extracting text', {
          title: 'Error',
          variant: 'danger',
        });
      } finally {
        this.isExtracting = false;
      }
    },

    copyAllText() {
      navigator.clipboard.writeText(this.extractedText);
      this.$bvToast.toast('Text copied to clipboard', {
        title: 'Success',
        variant: 'success',
        autoHideDelay: 2000,
      });
    },

    downloadAsText() {
      this.downloadFile(this.extractedText, 'extracted-claims.txt', 'text/plain');
    },

    downloadHistoryAsText(item) {
      this.downloadFile(item.result, `extracted-claims-${item.id}.txt`, 'text/plain');
    },

    downloadFile(content, filename, mimeType) {
      const blob = new Blob([content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.$bvToast.toast(`File downloaded: ${filename}`, {
        title: 'Success',
        variant: 'success',
        autoHideDelay: 2000,
      });
    },

    close() {
      this.activeTab = 0;
      this.selectedHistory = null;
      this.promptExpanded = false;
      this.$emit('close');
      this.$bvModal.hide(MODAL_ID);
    },

    async addImageUrlToDropzone(imageUrl) {
      if (!imageUrl) return;

      const dz = this.$refs.fileInput?.dropzone;
      if (!dz) {
        console.warn('Dropzone not ready');
        return;
      }

      dz.removeAllFiles(true);

      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const cleanUrl = imageUrl.split('?')[0];
      const fileName = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);

      const file = new File([blob], fileName, { type: blob.type });

      dz.emit('addedfile', file);
      dz.emit('thumbnail', file, URL.createObjectURL(blob));
      dz.emit('complete', file);
      dz.files.push(file);

      this.selectedFile = file;
    },
  },
};
</script>

<style scoped lang="scss">
.extract-tab {
  min-height: 300px;
}

.prompt-section {
  .prompt-header {
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #e9ecef !important;
    }
  }
}

.extracted-text-container {
  background-color: #f8f9fa;
  max-height: 400px;
  overflow-y: auto;

  pre {
    white-space: pre-wrap;
    word-wrap: break-word;
    font-size: 0.9rem;
  }
}

.history-tab {
  min-height: 300px;
}

.history-detail p {
  white-space: pre-wrap;
}

.history-item {
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f8f9fa;

    .delete-btn {
      opacity: 1;
    }
  }

  .delete-btn {
    opacity: 0;
    transition: opacity 0.2s;
    flex-shrink: 0;
  }

  .history-text {
    font-size: 0.9rem;
    line-height: 1.4;
  }
}

::v-deep .modal-dialog {
  max-width: 800px;
}

::v-deep .nav-tabs {
  border-bottom: none;
  display: flex;
  gap: 14px;
  margin-bottom: 20px;

  .nav-link {
    background-color: transparent;
    min-width: 79px;
    padding: 8px 16px;
    font-weight: 700;
    font-size: 14px;
    border-radius: 12px;
    border: none;

    &.active {
      background-color: #007964;
      color: #ffffff;
    }
  }
}

.cursor-pointer {
  cursor: pointer;
}


</style>

<style>
#excelDropzone .dz-preview {
  width: 200px !important;
  height: 200px !important;
  margin: 10px auto;
}

#excelDropzone .dz-image {
  width: 100% !important;
  height: 100% !important;
  border-radius: 8px;
  overflow: hidden;
}

#excelDropzone .dz-image img {
  width: 100%;
  height: 100%;
  object-fit: contain; /* <-- MOST IMPORTANT */
  background-color: #f8f9fa;
}

#excelDropzone .dz-filename {
  display: none;
}

#excelDropzone .dz-remove {
  text-align: center;
  margin-top: 6px;
}
</style>
